import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import WaterMark from "../WaterMark/WaterMark";
import { Loader } from "rsuite";
import { TbHandClick } from "react-icons/tb";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { axInst } from "../Login/Service";

const region_table_details = async (body, endpoint) => {
  try {
    const res = await axInst.post(endpoint + "region_wise_tqp", body);
    const obj = res.data;

    const TQP_Actual_MTD_sum = Object.values(obj)
      .reduce((sum, e) => sum + e.TQP_Actual_MTD, 0)
      .toFixed(1);

    const TQP_Target_MTD_sum = Object.values(obj)
      .reduce((sum, e) => sum + e.TQP_Target_MTD, 0)
      .toFixed(1);

    const C3_MTD_Retail_sum = Object.values(obj)
      .reduce((sum, e) => sum + e.C3_MTD_Retail, 0)
      .toFixed(1);

    const C3_MTD_Target_sum = Object.values(obj)
      .reduce((sum, e) => sum + e.C3_MTD_Target, 0)
      .toFixed(1);

    const Mtd_sum = ((TQP_Actual_MTD_sum / TQP_Target_MTD_sum) * 100).toFixed(
      1
    );
    const velocity_sum = (
      (C3_MTD_Retail_sum / TQP_Actual_MTD_sum) *
      100
    ).toFixed(1);

    const formattedData = Object.keys(obj).map((region) => ({
      Region: region,
      TQP_Actual_MTD: obj[region].TQP_Actual_MTD,
      TQP_Target_MTD: obj[region].TQP_Target_MTD,
      C3_MTD_Retail: obj[region].C3_MTD_Retail,
      C3_MTD_Target: obj[region].C3_MTD_Target,
      TQP_MTD: obj[region].TQP_MTD,
      TQP_VELOCITY: obj[region].TQP_VELOCITY,
    }));

    return {
      obj: formattedData,
      TQP_Actual_MTD_sum,
      TQP_Target_MTD_sum,
      C3_MTD_Retail_sum,
      C3_MTD_Target_sum,
      Mtd_sum,
      velocity_sum,
    };
  } catch (error) {
    console.error(error);
    return {
      obj: [],
      TQP_Actual_MTD_sum: 0,
      TQP_Target_MTD: 0,
      TQP_Target_MTD_sum: 0,
      C3_MTD_Retail_sum: 0,
      C3_MTD_Target_sum: 0,
      Mtd_sum: 0,
      velocity_sum: 0,
    };
  }
};

const state_table_details = async (region, endpoint) => {
  try {
    const res = await axInst.post(endpoint + "state_wise_tqp", {
      region: [region],
    });
    return res.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

const dealerwise_table_details = async (state, endpoint) => {
  try {
    const res = await axInst.post(endpoint + "dealer_wise_tqp", {
      state: [state],
    });
    return res.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

const TwoDrilldownTable = ({
  heading,
  columns,
  endpoint,
  QueryKey,
  className,
}) => {
  const { data: filterdata, submitted } = useSelector(
    (state) => state.filtersLeadSource
  );
  const { data, isFetching, isError } = useQuery({
    queryKey: [QueryKey, submitted],
    queryFn: () => region_table_details(filterdata, endpoint, QueryKey),
    initialData: {
      obj: [],
      TQP_Actual_MTD_sum: 0,
      TQP_Target_MTD: 0,
      TQP_Target_MTD_sum: 0,
      C3_MTD_Retail_sum: 0,
      C3_MTD_Target_sum: 0,
    },
    cacheTime: 0,
    staleTime: 0,
  });

  const [sorting, setSorting] = useState([]);
  const [filtering, setFiltering] = useState("");
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    return () => {
      setExpandedRows({});
    };
  }, []);

  const toggleRowExpanded = async (region, state = null) => {
    try {
      if (state) {
        if (!expandedRows[region][state]?.isExpanded) {
          const dealerData = await dealerwise_table_details(
            state,
            endpoint,
            QueryKey
          );

          setExpandedRows((prev) => ({
            ...prev,
            [region]: {
              ...prev[region],
              [state]: {
                ...prev[region][state],
                isExpanded: true,
                dealerData: dealerData,
              },
            },
          }));
        } else {
          setExpandedRows((prev) => ({
            ...prev,
            [region]: {
              ...prev[region],
              [state]: {
                ...prev[region][state],
                isExpanded: false,
              },
            },
          }));
        }
      } else {
        if (!expandedRows[region]) {
          const stateData = await state_table_details(
            region,
            endpoint,
            QueryKey
          );
          const stateExpansion = Object.keys(stateData).reduce(
            (acc, key) => ({
              ...acc,
              [key]: {
                isExpanded: false,
                data: stateData[key] || {},
              },
            }),
            {}
          );

          setExpandedRows((prev) => ({
            ...prev,
            [region]: stateExpansion,
          }));
        } else {
          setExpandedRows((prev) => ({
            ...prev,
            [region]: false,
          }));
        }
      }
    } catch (error) {
      console.error("Error toggling row expansion:", error);
    }
  };

  const table = useReactTable({
    data: data?.obj ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting: sorting,
      globalFilter: filtering,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
  });

  return (
    <div className={` relative  ${className}`}>
      <div className="heading pb-1 text-[15px] font-semibold text-[#4c4c4c]">
        {heading}
      </div>
      <div className="self-center drilltable max-h-[18rem] ">
        {isError && (
          <div className="grid items-center justify-center w-full h-full">
            <p>Error Occurred</p>
          </div>
        )}
        {isFetching && (
          <div className="grid items-center justify-center w-full h-full">
            <Loader />
          </div>
        )}
        {!isFetching && !isError && !data && (
          <div className="grid items-center justify-center w-full h-full">
            <p>Data Not Available</p>
          </div>
        )}
        {!isFetching && !isError && data && (
          <>
            <WaterMark />
            <table className="w-full border-collapse">
              <thead className="sticky text-white top-0 bg-[#2A5775] text-left z-[1]">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => (
                      <th
                        key={header.id}
                        className={`p-[1px]  text-[12px] border text-center border-gray-300 bg-[#2A5775] ${
                          index === 0
                            ? "w-[80px] sticky left-0 top-0 z-20 border-r border-b "
                            : "z-auto border-b"
                        }`}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, rowIndex) => (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`even:bg-gray-200 odd:bg-white ${
                        rowIndex === 0 ? "cursor-default" : "cursor-pointer"
                      } ${
                        expandedRows[row.original.Region] ? "font-bold" : ""
                      }`}
                      onClick={() =>
                        row.original.Region !== "All Regions" &&
                        toggleRowExpanded(row.original.Region)
                      }
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => (
                        <td
                          key={cell.id}
                          className={`p-[1px] text-[13px] text-center whitespace-nowrap  border border-gray-300 h-[25px]    ${
                            cellIndex === 0
                              ? "sticky left-0  bg-[#d4e1ec]  border-r "
                              : "z-auto"
                          }`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                          {cellIndex === 0 &&
                            row.original.Region !== "All Regions" && (
                              <div className="absolute right-0 top-2">
                                {expandedRows[row.original.Region] ? (
                                  <IoMdArrowDropup size={14} />
                                ) : (
                                  <IoMdArrowDropdown size={14} />
                                )}
                              </div>
                            )}
                        </td>
                      ))}
                    </tr>
                    {expandedRows[row.original.Region] &&
                      Object.entries(expandedRows[row.original.Region]).map(
                        ([state, stateDetails]) => (
                          <React.Fragment key={state}>
                            <tr
                              className={`bg-[#2A5775] text-white ${
                                stateDetails.isExpanded
                                  ? "font-bold"
                                  : "font-normal"
                              }`}
                              onClick={() =>
                                toggleRowExpanded(row.original.Region, state)
                              }
                            >
                              <td className="p-1 text-sm whitespace-nowrap text-center lg:text-sm border border-gray-300 h-8  bg-[#2A5775] sticky left-0 ">
                                <span className="ml-2 mr-4 ">{state}</span>
                                <div className="absolute right-0 top-2">
                                  {stateDetails.isExpanded ? (
                                    <IoMdArrowDropup size={14} />
                                  ) : (
                                    <IoMdArrowDropdown size={14} />
                                  )}
                                </div>
                              </td>
                              <td className="p-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                                {stateDetails.data.TQP_Target_MTD}
                              </td>
                              <td className="p-1 text-xs text-center  lg:text-sm border border-gray-300 h-8">
                                {stateDetails.data.TQP_Actual_MTD}
                              </td>
                              <td className="p-1 text-xs text-center  lg:text-sm border border-gray-300 h-8">
                                {stateDetails.data.TQP_MTD}
                              </td>
                              <td className="p-1 text-xs text-center  lg:text-sm border border-gray-300 h-8">
                                {stateDetails.data.TQP_VELOCITY}
                              </td>
                              <td className="p-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                                {stateDetails.data.C3_MTD_Target}
                              </td>
                              <td className="p-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                                {stateDetails.data.C3_MTD_Retail}
                              </td>
                            </tr>
                            {stateDetails.isExpanded &&
                              Object.entries(stateDetails.dealerData).map(
                                ([dealer, dealerDetails]) => (
                                  <tr
                                    key={dealer}
                                    className="even:bg-gray-50 odd:bg-white text-[12px]"
                                  >
                                    <td className="p-1 text-sm text-left  lg:text-sm border border-gray-300 h-8 sticky left-0  bg-white">
                                      <span className="ml-2 text-[12px] whitespace-nowrap">
                                        {dealer}
                                      </span>
                                    </td>
                                    <td className="p-1  text-xs text-center lg:text-sm border border-gray-300 h-8  ">
                                      {dealerDetails.TQP_Target_MTD}
                                    </td>
                                    <td className="p-1  text-xs text-center lg:text-sm border border-gray-300 h-8 ">
                                      {dealerDetails.TQP_Actual_MTD}
                                    </td>
                                    <td className="p-1  text-xs text-center lg:text-sm border border-gray-300 h-8 ">
                                      {dealerDetails.TQP_MTD}
                                    </td>
                                    <td className="p-1  text-xs text-center lg:text-sm border border-gray-300 h-8 ">
                                      {dealerDetails.TQP_VELOCITY}
                                    </td>

                                    <td className="p-1  text-xs text-center lg:text-sm border border-gray-300 h-8 ">
                                      {dealerDetails.C3_MTD_Target}
                                    </td>
                                    <td className="p-1  text-xs text-center lg:text-sm border border-gray-300 h-8 ">
                                      {dealerDetails.C3_MTD_Retail}
                                    </td>
                                  </tr>
                                )
                              )}
                          </React.Fragment>
                        )
                      )}
                  </React.Fragment>
                ))}
                <tr className="bg-[#1C5982]  text-center text-white lg:text-sm">
                  <td className="p-[2px] text-[12px]  border border-gray-300 sticky left-0 z-10 bg-[#1C5982]">
                    Total
                  </td>
                  <td className="p-[2px] text-[12px] border border-gray-300 ">
                    {data?.TQP_Target_MTD_sum ?? 0}
                  </td>
                  <td className="p-[2px] text-[12px]  border border-gray-300 ">
                    {data?.TQP_Actual_MTD_sum ?? 0}
                  </td>
                  <td className="p-[2px] text-[12px]  border border-gray-300 ">
                    {data?.Mtd_sum ?? 0}
                  </td>
                  <td className="p-[2px] text-[12px]  border border-gray-300 ">
                    {data?.velocity_sum ?? 0}
                  </td>

                  <td className="p-[2px] text-[12px]  border border-gray-300 ">
                    {data?.C3_MTD_Target_sum ?? 0}
                  </td>
                  <td className="p-[2px] text-[12px]  border border-gray-300 ">
                    {data?.C3_MTD_Retail_sum ?? 0}
                  </td>
                </tr>
              </tbody>
            </table>

            {QueryKey === "pipeline_lead" ? (
              <span className="text-xs">
                For Pipeline, only MTD C0, MTD C1, MTD C1A, and MTD C2 are
                considered.
              </span>
            ) : (
              <p className="text-xs mt-1 flex items-center">
                <TbHandClick /> on Region to view States, Dealer Wise
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TwoDrilldownTable;
